import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { MessageCircle, ArrowRight, Diamond, Award, Sparkles, Heart } from 'lucide-react';

export const DimantaAuskariRiga = () => {
  return (
    <>
      <Helmet>
        <title>Dimanta auskari Rīgā | Auskari ar dimantiem | Pure Atelier</title>
        <meta name="description" content="Eleganti dimanta auskari Rīgā. Auskari ar dabīgiem dimantiem, GIA sertifikāti, labākās cenas Latvijā. Bezmaksas konsultācija Pure Atelier." />
        <meta name="keywords" content="dimanta auskari, auskari ar dimantiem, dimanta auskari Rīgā, diamond earrings Riga" />
        <link rel="canonical" href="https://pureatelier.lv/dimanta-auskari-riga" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-[url('https://images.pexels.com/photos/10983780/pexels-photo-10983780.jpeg?auto=compress&cs=tinysrgb&w=1920')] bg-cover bg-center opacity-10"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block px-4 py-2 bg-amber-100 text-amber-800 rounded-full text-sm font-medium mb-6">
                Pure Atelier Riga
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-stone-900 mb-6 leading-tight">
                Dimanta auskari Rīgā
              </h1>
              <p className="text-xl text-stone-600 mb-8 max-w-2xl mx-auto">
                Izsmalcināti auskari ar dabīgiem dimantiem. Elegance un kvalitāte katrai sievietei.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/gallery?category=earrings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Skatīt auskaru kolekciju <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <a href="https://wa.me/37128448991?text=Sveiki!%20Interesē%20dimanta%20auskari." target="_blank" rel="noopener noreferrer">
                  <Button size="lg" variant="outline" className="border-amber-600 text-amber-700 hover:bg-amber-50 px-8">
                    <MessageCircle className="mr-2 h-5 w-5" /> Bezmaksas konsultācija
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8">
              {[
                { icon: Diamond, title: "Sertificēti dimanti", desc: "GIA un IGI sertifikāti" },
                { icon: Award, title: "Augsta kvalitāte", desc: "Roku darbs Latvijā" },
                { icon: Sparkles, title: "Dažādi stili", desc: "Pusetes, piekarināmie" },
                { icon: Heart, title: "Ideāla dāvana", desc: "Skaistā iepakojumā" }
              ].map((item, i) => (
                <div key={i} className="text-center p-6">
                  <item.icon className="h-12 w-12 text-amber-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-stone-900 mb-2">{item.title}</h3>
                  <p className="text-stone-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-stone-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-serif text-stone-900 mb-8 text-center">
                Dimanta auskari no Pure Atelier
              </h2>
              
              <div className="prose prose-lg max-w-none text-stone-700">
                <p>
                  <strong>Pure Atelier</strong> piedāvā ekskluzīvus dimanta auskarus Rīgā. Mūsu kolekcijā atradīsiet 
                  gan klasiskās pusetes (stud earrings), gan elegantākus piekarināmos auskarus.
                </p>
                
                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Mūsu auskaru kolekcija</h3>
                <ul>
                  <li><strong>Dimanta pusetes</strong> — klasiskās 3 un 4 nadziņu pusetes ar vienu dimantu</li>
                  <li><strong>Halo auskari</strong> — ar papildu dimantiem ap galveno akmeni</li>
                  <li><strong>Piekarināmie auskari</strong> — eleganti auskari īpašiem gadījumiem</li>
                </ul>

                <h3 className="text-2xl font-serif text-stone-900 mt-8 mb-4">Kāpēc izvēlēties Pure Atelier?</h3>
                <p>
                  Mēs piedāvājam tikai augstākās kvalitātes dabīgos dimantus ar starptautiskiem sertifikātiem. 
                  Katrs auskaru pāris tiek rūpīgi izvēlēts, lai dimanti būtu identiski pēc krāsas un tīrības.
                </p>
                <ul>
                  <li>Sertificēti dabīgie dimanti</li>
                  <li>Labākās cenas Latvijā</li>
                  <li>Skaists dāvanu iepakojums</li>
                  <li>Bezmaksas konsultācija</li>
                </ul>
              </div>

              <div className="mt-12 text-center">
                <Link to="/gallery?category=earrings">
                  <Button size="lg" className="bg-amber-600 hover:bg-amber-700 text-white px-8">
                    Apskatīt visus auskarus <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-stone-900 text-white">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl font-serif mb-4">Meklējat perfektus dimanta auskarus?</h2>
            <p className="text-stone-300 mb-8 max-w-2xl mx-auto">
              Sazināties ar mums, lai saņemtu personalizētu piedāvājumu.
            </p>
            <a href="https://wa.me/37128448991?text=Sveiki!%20Interesē%20dimanta%20auskari." target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-green-600 hover:bg-green-700 text-white px-8">
                <MessageCircle className="mr-2 h-5 w-5" /> WhatsApp konsultācija
              </Button>
            </a>
          </div>
        </section>
      </div>
    </>
  );
};

export default DimantaAuskariRiga;
